import setOngoingActionAndActionMessage from './setOngoingActionAndActionMessage';
import ActionMessageId from '../schema/ActionMessageId';
import ActionType from '../schema/ActionType';
import AttachmentFullViewState from '../schema/AttachmentFullViewState';
import convertClassicToCloudyAttachment from '../services/convertClassicToCloudyAttachment';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import { subscribeNotificationAndGetChannelId } from 'owa-attachment-web-notification';
import { ClientAttachmentId } from 'owa-client-ids';
import { lazyUnsubscribe } from 'owa-notification';
import AttachmentInfoResponseMessage from 'owa-service/lib/contract/AttachmentInfoResponseMessage';
import AttachmentResultCode from 'owa-service/lib/contract/AttachmentResultCode';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import ConvertLocalToRefAttachmentNotificationPayload from 'owa-service/lib/contract/ConvertLocalToRefAttachmentNotificationPayload';
import ItemId from 'owa-service/lib/contract/ItemId';
import { action } from 'satcheljs/lib/legacy';

const convertClassicToCloudyCancellationMap: { [attachmentId: string]: string } = {};
export default action('convertClassicToCloudy')(async function convertClassicToCloudy(
    attachment: AttachmentFullViewState,
    parentItemId: ItemId,
    completeHandler: (succeeded: boolean, attachmentType: AttachmentType) => void
) {
    const datapoint: PerformanceDatapoint = new PerformanceDatapoint(
        'AttachmentConvertClassicToCloudy'
    );

    convertClassicToCloudyPreProcess(attachment);
    const notificationHandler = (
        payload: ConvertLocalToRefAttachmentNotificationPayload
    ): boolean => {
        convertClassicToCloudyPostProcess(attachment, payload.ResultCode);
        logConvertClassicToCloudyDatapoint(
            datapoint,
            attachment.attachmentId,
            payload.ResultCode,
            payload.Exception
        );
        if (
            payload.ResultCode === AttachmentResultCode.Success &&
            payload.Response &&
            payload.Response.ResponseMessages &&
            payload.Response.ResponseMessages.Items &&
            payload.Response.ResponseMessages.Items.length > 0 &&
            payload.Response.ResponseMessages.Items[0].ResponseCode === 'NoError'
        ) {
            const responseMessage: AttachmentInfoResponseMessage =
                payload.Response.ResponseMessages.Items[0];
            completeHandler(true, responseMessage.Attachments[0]);
        } else {
            completeHandler(false, null);
        }
        return true;
    };
    const serviceCallFailureCallback = (error: string): void => {
        convertClassicToCloudyPostProcess(attachment, AttachmentResultCode.GenericFailure);
        completeHandler(false, null);
        logConvertClassicToCloudyDatapoint(
            datapoint,
            attachment.attachmentId,
            AttachmentResultCode.GenericFailure,
            error
        );
    };

    await invokeConvertClassicToCloudyService(
        attachment.attachmentId,
        parentItemId,
        notificationHandler,
        serviceCallFailureCallback
    );
});

export function getCancellationId(attachmentId: string): string {
    return convertClassicToCloudyCancellationMap[attachmentId];
}

async function invokeConvertClassicToCloudyService(
    attachmentId: ClientAttachmentId,
    parentItemId: ItemId,
    notificationHandler: (notification: ConvertLocalToRefAttachmentNotificationPayload) => boolean,
    serviceCallFailureCallback: (error: string) => void
) {
    const [
        subscriptionId,
        subscription,
        handleAttachmentNofication,
        channelId,
    ] = await subscribeNotificationAndGetChannelId(notificationHandler);
    convertClassicToCloudyCancellationMap[attachmentId.Id] = subscriptionId;
    try {
        await convertClassicToCloudyAttachment(
            attachmentId.Id,
            parentItemId,
            subscriptionId,
            channelId,
            subscriptionId // subscriptionId is guid so we reuse it as cancellationId
        );
    } catch (error) {
        serviceCallFailureCallback(error.message);
        lazyUnsubscribe.importAndExecute(subscription, handleAttachmentNofication);
    }
}

export function convertClassicToCloudyPreProcess(attachment: AttachmentFullViewState) {
    setOngoingActionAndActionMessage(
        attachment,
        ActionType.ConvertClassicToCloudy,
        ActionMessageId.ConvertClassicToCloudyInProgress
    );
}

export function convertClassicToCloudyPostProcess(
    attachment: AttachmentFullViewState,
    resultCode: AttachmentResultCode
) {
    switch (resultCode) {
        case AttachmentResultCode.Success:
            break;
        case AttachmentResultCode.Cancelled:
            setOngoingActionAndActionMessage(attachment, ActionType.None, ActionMessageId.None);
            break;
        default:
            setOngoingActionAndActionMessage(
                attachment,
                ActionType.None,
                ActionMessageId.ConvertClassicToCloudyFailedWithIcon
            );
            break;
    }
    delete convertClassicToCloudyCancellationMap[attachment.attachmentId.Id];
}

function logConvertClassicToCloudyDatapoint(
    datapoint: PerformanceDatapoint,
    attachmentId: ClientAttachmentId,
    resultCode: AttachmentResultCode,
    exception: string
) {
    const attachmentModel = getAttachment(attachmentId);
    datapoint.addCustomData([attachmentModel.model.Size, resultCode]);
    if (
        resultCode === AttachmentResultCode.Success ||
        resultCode === AttachmentResultCode.Cancelled
    ) {
        datapoint.end();
    } else {
        datapoint.endWithError(DatapointStatus.ServerError, new Error(exception));
    }
}
